import type { ReactNode } from "react";
import { Command as CommandPrimitive } from "cmdk";
import type { Icon } from "@phosphor-icons/react";

/**
 * FRONT-2436: the command palette's shared building blocks — section eyebrow,
 * result row, preview-pane chrome, query highlighting — lifted out of
 * `command-palette.tsx` unchanged so a sibling module (the build-item sections
 * in `command-palette-build-items.tsx`) renders with the exact same pieces
 * instead of a copy of them.
 */

/**
 * Pure: splits `text` into segments marking every case-insensitive occurrence
 * of `query` (trimmed) — used to highlight the typed query inline in a row's
 * title/snippet (FRONT-874 fidelity fix) the same way `parseSnippet` marks a
 * server-side `<b>` highlight, just driven by client-side matching instead of
 * `ts_headline` output. An empty (or whitespace-only) query yields a single
 * non-matching segment covering the whole string, so callers can render
 * unconditionally without a special-case branch. Exported for unit testing.
 */
export function splitByQuery(text: string, query: string): Array<{ text: string; match: boolean }> {
  const q = query.trim();
  if (!q) return [{ text, match: false }];
  const lowerText = text.toLowerCase();
  const lowerQuery = q.toLowerCase();
  const segments: Array<{ text: string; match: boolean }> = [];
  let i = 0;
  while (i < text.length) {
    const idx = lowerText.indexOf(lowerQuery, i);
    if (idx === -1) {
      segments.push({ text: text.slice(i), match: false });
      break;
    }
    if (idx > i) segments.push({ text: text.slice(i, idx), match: false });
    segments.push({ text: text.slice(idx, idx + q.length), match: true });
    i = idx + q.length;
  }
  return segments;
}

/** A section eyebrow: thin primary rule + uppercase label + mono count — the
 *  signature motif reused across the app's zen surfaces.
 *
 *  `testId` (FRONT-2391) is optional and unused by any section before Recent
 *  activity — those are addressed by accessible name in specs today. The
 *  Recent sections carry one (`palette-recent-<kind>`) so a browser spec can
 *  find them without matching model-authored/user-authored row text. */
export function PaletteSection({
  label,
  count,
  testId,
  children,
}: {
  label: string;
  count: number;
  testId?: string;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1" data-testid={testId}>
      <div className="mb-2 ml-1 flex items-center gap-2 text-xs font-medium tracking-[0.08em] text-muted-foreground uppercase">
        <span className="h-px w-[22px] shrink-0 bg-primary" />
        {label}
        <span className="font-mono text-[length:var(--text-2xs)] font-normal tracking-normal text-muted-foreground/70 normal-case">
          · {count}
        </span>
      </div>
      <div className="flex flex-col gap-0.5">{children}</div>
    </div>
  );
}

/** A single result row: icon tile, title + optional snippet, right-aligned
 *  meta. Selected state (cmdk's own `data-selected`) drives a muted fill plus
 *  a 2px primary left rail; hovering a row that ISN'T selected paints only a
 *  faint background (FRONT-874 review fix — hover must never look like
 *  selection, since pointer-move selection is now disabled on the `Command`
 *  root; see `handleListClickCapture` below for why a click alone no longer
 *  opens anything). A double-click reuses the exact same `onSelect` handler
 *  Enter fires, per the approved mockup's interaction model. */
export function PaletteRow({
  value,
  icon: RowIcon,
  title,
  snippet,
  meta,
  onSelect,
  testId,
}: {
  value: string;
  icon: Icon;
  title: ReactNode;
  snippet?: ReactNode;
  meta?: ReactNode;
  onSelect: () => void;
  /** FRONT-2391 — see `PaletteSection`'s own `testId` doc above. */
  testId?: string;
}) {
  return (
    <CommandPrimitive.Item
      value={value}
      data-testid={testId}
      onSelect={onSelect}
      onDoubleClick={onSelect}
      className="group/row relative flex cursor-pointer items-center gap-[11px] rounded-xl px-[11px] py-[9px] outline-none select-none hover:bg-muted/50 data-selected:bg-muted"
    >
      <span className="pointer-events-none absolute inset-y-2 left-0 w-0.5 rounded-full bg-primary opacity-0 group-data-selected/row:opacity-100" />
      <span className="grid size-[30px] shrink-0 place-items-center rounded-[9px] border border-border bg-muted text-muted-foreground">
        <RowIcon size={16} weight="regular" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[14px] font-medium text-foreground">{title}</span>
        {snippet && (
          <span className="block truncate text-[12.5px] text-muted-foreground">{snippet}</span>
        )}
      </span>
      {meta && <span className="shrink-0">{meta}</span>}
    </CommandPrimitive.Item>
  );
}

export function RowMeta({ children }: { children: ReactNode }) {
  return <span className="font-mono text-[length:var(--text-2xs)] text-muted-foreground">{children}</span>;
}

export function PalettePill({ children }: { children: ReactNode }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-primary/25 bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
      {children}
    </span>
  );
}

/** The pills + mono-date row beneath the preview title — bordered on the
 *  bottom, exactly `#vB .prev .byline` in the approved mockup (FRONT-874
 *  review fix, item 6). Chats show a message-count pill + attachment pills;
 *  documents show tag pills; files show type + size pills — all followed by
 *  the same right-trailing mono date. */
export function PreviewByline({ date, children }: { date?: string; children: ReactNode }) {
  return (
    <div className="mt-3.5 flex flex-wrap items-center gap-2 border-b border-border pb-[18px]">
      {children}
      {date && <span className="font-mono text-xs text-[var(--ink-3)]">{date}</span>}
    </div>
  );
}

/** Shared styling for the preview pane's clickable "open" affordances — used
 *  by both `OpenAffordance` (a `<button>` tied to the selected row's `onOpen`
 *  handler) and `OpenFileLinkAffordance` (FRONT-1642: a real `<a>` to the raw
 *  file, rendered ONLY alongside "Open text" for a hit that has both
 *  original bytes and extracted text — see `shouldSplitFileActions`).
 *  Factored out so the two element types share one class string instead of
 *  a hand-copied second one. Deliberately excludes the `mt-[22px]` top
 *  margin — callers apply that themselves, since the two-action case needs
 *  it on a wrapping row rather than on each action individually. */
export const OPEN_AFFORDANCE_CLASS =
  "inline-flex cursor-pointer items-center gap-2 rounded-sm text-[13px] font-medium text-primary outline-none transition-colors hover:underline focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50";

/** The mockup's "↵ Open …" control at the bottom of the preview pane — a
 *  real clickable affordance wired to the exact same handler Enter/click
 *  would fire for the currently-selected row (FRONT-874 review fix, item 3),
 *  not just whichever row the mouse happens to be over. Hover/focus styling
 *  matches the app's existing primary-colored text-link idiom (e.g.
 *  `plan-card.tsx`'s "Open in graph", `AccordionTrigger`'s
 *  `hover:underline focus-visible:ring-[3px]`) so it reads as a real button
 *  under the mouse instead of static text.
 *
 *  `withMargin` (default true) drops the top margin when a caller supplies
 *  its own (FRONT-1642: the file preview's two-action row). Every OTHER
 *  caller (Page/Work/Memory/Chat/Document previews, and a file hit that
 *  doesn't split — see `FilePreview`) renders this exactly as it always
 *  has: `label="Open file"`/etc with the ↵ badge, no anchor, unchanged. */
export function OpenAffordance({
  label,
  onOpen,
  withMargin = true,
}: {
  label: string;
  onOpen?: () => void;
  withMargin?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onOpen}
      disabled={!onOpen}
      className={`${withMargin ? "mt-[22px] " : ""}${OPEN_AFFORDANCE_CLASS}`}
    >
      <kbd className="rounded-md border border-primary/25 bg-primary/10 px-1.5 py-0.5 font-mono text-[length:var(--text-2xs)]">
        ↵
      </kbd>
      {label}
    </button>
  );
}

export function PreviewEyebrow({ label, date }: { label: string; date?: string }) {
  return (
    <div className="flex items-center gap-2 text-xs font-medium tracking-[0.08em] text-primary uppercase">
      <span className="h-px w-[22px] shrink-0 bg-primary" />
      {label}
      {date && (
        <span className="font-mono text-[length:var(--text-2xs)] font-normal tracking-normal text-muted-foreground normal-case">
          · {date}
        </span>
      )}
    </div>
  );
}

export function PreviewTitle({ children }: { children: ReactNode }) {
  return (
    <h2 className="mt-3.5 text-[length:var(--text-2xl)] leading-tight font-semibold tracking-[-0.012em] text-foreground">
      {children}
    </h2>
  );
}

/** Wraps every case-insensitive occurrence of `query` in `text` with the
 *  mockup's tinted-pill highlight (`.row .title mark`) — used for every
 *  result row's title (Navigate/Account and Work/Memory/Chats/Files/Documents
 *  alike) so the typed query visually pops in the list, not just the preview
 *  pane. Thin presentational wrapper around the pure `splitByQuery`. */
export function HighlightedTitle({ text, query }: { text: string; query: string }) {
  return (
    <>
      {splitByQuery(text, query).map((seg, i) =>
        seg.match ? (
          <mark key={i} className="rounded-[3px] bg-primary/15 px-[2px] font-semibold text-primary">
            {seg.text}
          </mark>
        ) : (
          <span key={i}>{seg.text}</span>
        ),
      )}
    </>
  );
}

/** Primary-colored, semibold, no-background highlight for row snippets
 *  (mockup's `.snippet mark`) — used for file/document rows, which have no
 *  server-side `<b>` highlight and so match client-side via `splitByQuery`
 *  against the typed query. Chat rows use `HighlightedSnippet` instead (the
 *  server already tells us where the match is). */
export function HighlightedQueryText({ text, query }: { text: string; query: string }) {
  return (
    <>
      {splitByQuery(text, query).map((seg, i) =>
        seg.match ? (
          <strong key={i} className="font-semibold text-primary">
            {seg.text}
          </strong>
        ) : (
          <span key={i}>{seg.text}</span>
        ),
      )}
    </>
  );
}

export function EmptyPreview({ message = "Select a result to preview it." }: { message?: string }) {
  return (
    <div className="flex h-full items-center justify-center text-center text-sm text-muted-foreground">
      {message}
    </div>
  );
}
